// CaptureSheet.tsx — the four-mode capture sheet (CAP-1.x)
//
// Modes:
//   1. Record meeting     -> ConsentGate -> Recorder -> create source -> resumable upload -> finalize
//   2. Quick voice memo    -> same flow, tagged capture_profile:"memo"
//   3. Snap a document     -> stub, disabled (native camera/OCR ships later)
//   4. Drop a link or text -> research-ingest-web (URL) OR a manual_text source (free text)
//
// The audio path routes the upload through offlineQueue so a capture started offline still completes
// when connectivity returns. The "No consent — notes only" escape inside ConsentGate diverts to a
// manual_text source (no audio ever leaves the device).
//
// Long-press the FAB opens the last-used mode directly (persisted via @capacitor/preferences).
// Capture status is polled via getCaptureStatus and shown inline.
//
// Accessibility + safe-area aware + teal styling per the mobile shell.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Mic,
  MessageSquare,
  Camera,
  Link2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  WifiOff,
  CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getIsOnline, useIsOnline } from '@/hooks/useIsOnline';
import { getCachedSync, setCached } from '@/lib/offline/persistentCache';
import { supabase } from '@/integrations/supabase/client';
import { enqueue, updateState, remove } from '@/lib/native/offlineQueue';
import { uploadResumable } from '@/lib/native/resumableUpload';
import {
  audioExtensionForMime,
  createMobileSource,
  finalizeUpload,
  getCaptureStatus,
  getLastEvent,
  persistLastEvent,
  type Consent,
  type StickyEvent,
} from '@/lib/capture';
import { ConsentGate } from './ConsentGate';
import { Recorder } from './Recorder';
import { EventPicker } from './EventPicker';
import type { RecorderResult } from './useRecorder';

// Persistent-cache key for a source's last-known status label (so reopening the sheet / a cold offline
// start shows the last status instead of a blank "Processing…").
const statusCacheKey = (sourceId: string) => `capture.status.${sourceId}`;

const LAST_MODE_KEY = 'cbam.capture.lastMode';

export type CaptureMode = 'record' | 'memo' | 'photo' | 'link';

type Step =
  | { kind: 'menu' }
  | { kind: 'consent'; mode: 'record' | 'memo' }
  | { kind: 'record'; mode: 'record' | 'memo'; consent: Consent }
  | { kind: 'link' }
  | { kind: 'uploading' }
  | { kind: 'done'; sourceId: string }
  // Audio captured but not yet uploaded (offline): it's safely queued and will upload on reconnect.
  | { kind: 'queued' }
  | { kind: 'error'; message: string };

interface ModeDef {
  key: CaptureMode;
  label: string;
  icon: typeof Mic;
  disabled?: boolean;
  hint?: string;
}

const MODES: ModeDef[] = [
  { key: 'record', label: 'Record meeting', icon: Mic, hint: 'Audio + transcript' },
  { key: 'memo', label: 'Quick voice memo', icon: MessageSquare, hint: 'Short note to self' },
  {
    key: 'photo',
    label: 'Snap a document',
    icon: Camera,
    disabled: true,
    hint: 'Coming soon',
  },
  { key: 'link', label: 'Drop a link or text', icon: Link2, hint: 'URL or typed note' },
];

async function persistLastMode(mode: CaptureMode) {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: LAST_MODE_KEY, value: mode });
  } catch {
    /* preferences unavailable (web) — ignore */
  }
}

export async function getLastCaptureMode(): Promise<CaptureMode | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: LAST_MODE_KEY });
    if (value === 'record' || value === 'memo' || value === 'link') return value;
  } catch {
    /* ignore */
  }
  return null;
}

export interface CaptureSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional mode to jump straight into (long-press → last-used mode). */
  initialMode?: CaptureMode | null;
}

export function CaptureSheet({ open, onClose, initialMode }: CaptureSheetProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const online = useIsOnline();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'menu' });

  // Sticky event selection: the event each new capture is pre-tagged to (conference / sub-event).
  // Hydrated from @capacitor/preferences (web: localStorage) on open so a new recording in a
  // conference is already tagged. `null` === no event. The picker overlay toggles via showEventPicker.
  const [selectedEvent, setSelectedEvent] = useState<StickyEvent | null>(null);
  const [showEventPicker, setShowEventPicker] = useState(false);

  // True once the user has selected/cleared an event this open. The async hydrate (getLastEvent)
  // checks this so a late-resolving read never clobbers a clear/select the user made first.
  const userTouchedEventRef = useRef(false);

  // Select (or clear) the sticky event and persist it for the next capture. Fire-and-forget persist.
  const selectEvent = useCallback((event: StickyEvent | null) => {
    userTouchedEventRef.current = true;
    setSelectedEvent(event);
    void persistLastEvent(event);
  }, []);

  // Status polling for the just-finalized source.
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  // True when polling can't reach the server (offline / transient) and we're showing a last-known or
  // pending label instead of a live one.
  const [statusFromCache, setStatusFromCache] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Reset to the requested entry step whenever the sheet opens.
  useEffect(() => {
    if (!open) return;
    if (initialMode === 'record' || initialMode === 'memo') {
      setStep({ kind: 'consent', mode: initialMode });
    } else if (initialMode === 'link') {
      setStep({ kind: 'link' });
    } else {
      setStep({ kind: 'menu' });
    }
    setStatusLabel(null);
    setStatusFromCache(false);
    setShowEventPicker(false);
    // New open → the user hasn't touched the event tag yet this session, so the hydrate below may set it.
    userTouchedEventRef.current = false;
  }, [open, initialMode]);

  // Hydrate the sticky event whenever the sheet opens (AUTO-LINK: the persisted value pre-selects the
  // event the user last tagged in this conference). Best-effort: a missing/cleared value leaves none.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getLastEvent().then((event) => {
      // Skip if unmounted/closed OR the user already selected/cleared an event before this resolved —
      // the read captured the pre-interaction value and must not clobber a deliberate user action.
      if (!cancelled && !userTouchedEventRef.current) setSelectedEvent(event);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleClose = useCallback(() => {
    stopPolling();
    onClose();
  }, [onClose, stopPolling]);

  // "View capture" on the done step (only offered when we actually have a sourceId — the link-ingest
  // path can land on `done` with an empty sourceId when the response didn't carry one). Close the sheet
  // first so it unmounts before navigating, matching the "Done" dismiss behaviour.
  const handleViewCapture = useCallback(
    (sourceId: string) => {
      handleClose();
      navigate(`/partner/m/source/${encodeURIComponent(sourceId)}`);
    },
    [handleClose, navigate],
  );

  const startStatusPolling = useCallback(
    (sourceId: string) => {
      stopPolling();

      // Seed instantly from the last-known status (survives a cold offline start / reopened sheet).
      if (sourceId) {
        const cached = getCachedSync<string>(statusCacheKey(sourceId));
        if (cached) {
          setStatusLabel(cached.data);
          setStatusFromCache(true);
        }
      }

      const tick = async () => {
        // Offline: don't hammer a dead network on the 4s interval. Surface the offline state and keep
        // the last-known label; the tick resumes live polling automatically once we're back online.
        if (!getIsOnline()) {
          setStatusFromCache(true);
          return;
        }
        try {
          const rows = await getCaptureStatus([sourceId]);
          const row = rows.find((r) => r.source_id === sourceId);
          if (row) {
            const label = row.status_detail || row.status;
            setStatusLabel(label);
            setStatusFromCache(false);
            void setCached(statusCacheKey(sourceId), label);
            if (['done', 'ready', 'complete', 'failed', 'blocked_consent'].includes(row.status)) {
              stopPolling();
            }
          }
        } catch {
          // Transient (incl. an offline read that slipped through) — keep the last label and flag it as
          // not-live so the UI can say "last known". The interval keeps polling.
          setStatusFromCache(true);
        }
      };
      void tick();
      pollRef.current = setInterval(tick, 4000);
    },
    [stopPolling],
  );

  // -------------------------------------------------------------------------
  // Audio capture flow (record / memo)
  // -------------------------------------------------------------------------

  const handleRecordingComplete = useCallback(
    async (mode: 'record' | 'memo', consent: Consent, rec: RecorderResult) => {
      if (!user?.id) {
        setStep({ kind: 'error', message: 'You must be signed in to capture audio.' });
        return;
      }
      setStep({ kind: 'uploading' });

      const captureUuid = crypto.randomUUID();
      // Snapshot the selected event id at record time so both the foreground create and the queued
      // re-drive tag the SAME event (the sticky selection could change before a reconnect drain runs).
      const eventId = selectedEvent?.eventId ?? null;
      // The createMobileSource args, defined once so the offline-drain path (drainCaptureQueue)
      // reconstructs the source — and its legal consent basis — identically to this foreground flow.
      const sourceArgs = {
        sourceType: 'manual_audio',
        title: mode === 'memo' ? 'Voice memo' : 'Meeting recording',
        rawMetadata: mode === 'memo' ? { capture_profile: 'memo' } : {},
      };
      // Stage the capture in the offline queue first so it survives an app kill / offline period.
      // Persist the FULL re-drive context (owner + consent + source args + event tag) in meta so a
      // headless reconnect drain can complete it without the UI — see src/lib/capture/drainCaptures.ts.
      await enqueue({
        id: captureUuid,
        state: 'pending_upload',
        contentType: rec.mimeType,
        blob: rec.blob,
        meta: { ownerId: user.id, mode, durationMs: rec.durationMs, consent, eventId, ...sourceArgs },
      });

      try {
        // 1. Create the server source (idempotent on captureUuid).
        const { sourceId } = await createMobileSource({
          captureUuid,
          consent,
          eventId,
          ...sourceArgs,
        });

        // Extension derived from the capture's MIME type (native → .m4a, web → .webm) via the shared
        // audioExtensionForMime helper — drainCaptures.objectPathFor uses the SAME helper so a queued
        // re-drive of this capture builds an identical object key.
        const objectPath = `mobile/${user.id}/${sourceId}/audio${audioExtensionForMime(rec.mimeType)}`;
        await updateState(captureUuid, 'uploading', { sourceId, objectPath });

        // 2. Resumable upload (survives flaky connectivity).
        await uploadResumable({
          data: rec.blob,
          objectPath,
          contentType: rec.mimeType,
        });

        // 3. Finalize → enqueues the transcription/extraction job.
        await updateState(captureUuid, 'finalizing', { sourceId });
        await finalizeUpload({ sourceId, captureUuid });

        // 4. Done — clear the queue item and start status polling.
        await updateState(captureUuid, 'done', { sourceId });
        await remove(captureUuid);

        setStep({ kind: 'done', sourceId });
        startStatusPolling(sourceId);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Upload failed.';
        await updateState(captureUuid, 'failed', { lastError: message });
        // Note: the item stays in the offline queue (failed) so drainPending can retry it later
        // (the reconnect/foreground drain trigger fires it automatically once back online).
        if (!getIsOnline()) {
          // Offline isn't an error — the capture is safely queued. Show the reassuring queued state.
          setStep({ kind: 'queued' });
          toast({
            title: 'Saved — will upload when online',
            description: 'You are offline. This capture is queued and uploads automatically on reconnect.',
          });
        } else {
          setStep({ kind: 'error', message });
          toast({
            title: 'Capture queued for retry',
            description: 'We could not finish the upload. It will retry when you are back online.',
            variant: 'destructive',
          });
        }
      }
    },
    [startStatusPolling, toast, user?.id, selectedEvent?.eventId],
  );

  // -------------------------------------------------------------------------
  // Link / text flow
  // -------------------------------------------------------------------------

  const [linkValue, setLinkValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [submittingLink, setSubmittingLink] = useState(false);

  const submitNotesOnly = useCallback(
    async (consentDeclined: boolean) => {
      // Used by both the consent "notes only" escape and the typed-text path.
      const body = textValue.trim();
      if (!body) {
        toast({ title: 'Nothing to save', description: 'Type a note first.' });
        return;
      }
      setStep({ kind: 'uploading' });
      const captureUuid = crypto.randomUUID();
      try {
        const { sourceId } = await createMobileSource({
          captureUuid,
          sourceType: 'manual_text',
          title: body.slice(0, 60),
          rawMetadata: {
            body,
            ...(consentDeclined ? { consent_declined: true } : {}),
          },
          eventId: selectedEvent?.eventId ?? null,
        });
        await finalizeUpload({ sourceId, captureUuid });
        setStep({ kind: 'done', sourceId });
        startStatusPolling(sourceId);
      } catch (e) {
        setStep({ kind: 'error', message: e instanceof Error ? e.message : 'Could not save note.' });
      }
    },
    [startStatusPolling, textValue, toast, selectedEvent?.eventId],
  );

  const submitLink = useCallback(async () => {
    const url = linkValue.trim();
    if (!url) return;
    // The link path is a live fetch (research-ingest-web) and is NOT queued like audio, so it can't be
    // staged offline. Tell the user plainly rather than letting it fail into a generic error.
    if (!getIsOnline()) {
      toast({
        title: 'You are offline',
        description: 'Links need a connection to fetch. Reconnect and try again, or type a note below (saved offline).',
        variant: 'destructive',
      });
      return;
    }
    setSubmittingLink(true);
    setStep({ kind: 'uploading' });
    try {
      const { data, error } = await supabase.functions.invoke('research-ingest-web', {
        body: { url },
      });
      if (error) throw error;
      const sourceId =
        (data as any)?.source_id ?? (data as any)?.sourceId ?? (data as any)?.id ?? null;
      if (sourceId) {
        setStep({ kind: 'done', sourceId });
        startStatusPolling(sourceId);
      } else {
        setStep({ kind: 'done', sourceId: '' });
      }
    } catch (e) {
      setStep({ kind: 'error', message: e instanceof Error ? e.message : 'Could not ingest link.' });
    } finally {
      setSubmittingLink(false);
    }
  }, [linkValue, startStatusPolling, toast]);

  // -------------------------------------------------------------------------
  // Mode selection
  // -------------------------------------------------------------------------

  const selectMode = useCallback((mode: CaptureMode) => {
    void persistLastMode(mode);
    if (mode === 'record' || mode === 'memo') {
      setStep({ kind: 'consent', mode });
    } else if (mode === 'link') {
      setStep({ kind: 'link' });
    }
  }, []);

  if (!open) return null;

  // -------------------------------------------------------------------------
  // Render — rendered through a portal so `fixed inset-0` resolves against the viewport (mirrors the
  // existing FAB sheet fix: never inside a transformed/clipped ancestor).
  // -------------------------------------------------------------------------

  const showBack =
    step.kind === 'consent' || step.kind === 'record' || step.kind === 'link';

  // The event chip shows on the capture-entry steps (menu / consent / record / link) so a capture can
  // be tagged before it's submitted, but not on the terminal/in-flight states, and not while the
  // picker overlay itself is open.
  const showEventChip =
    !showEventPicker &&
    (step.kind === 'menu' ||
      step.kind === 'consent' ||
      step.kind === 'record' ||
      step.kind === 'link');

  const body = (
    <div
      className="fixed inset-0 z-[60] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Capture"
    >
      <button
        type="button"
        aria-label="Close capture"
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />
      <div className="relative z-10 mx-auto max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-card p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl">
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-muted-foreground/30" />
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {showBack && (
              <button
                type="button"
                aria-label="Back"
                onClick={() => setStep({ kind: 'menu' })}
                className="rounded-full p-1 text-muted-foreground hover:bg-accent"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="font-heading text-base font-semibold">Capture</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* EVENT CHIP — tag this capture to a research_event. Hidden on terminal/overlay states so it
            doesn't distract once a capture has been submitted. Tapping the label opens the EventPicker
            overlay; the trailing "x" (when an event is set) drops the tag. The clear button is a
            sibling of the label button (not nested) so the markup stays valid. */}
        {showEventChip && (
          <div className="mb-3 flex w-full items-center gap-2 rounded-lg border border-border bg-muted/30 pr-1 text-xs">
            <button
              type="button"
              onClick={() => setShowEventPicker(true)}
              aria-label={
                selectedEvent
                  ? `Event: ${selectedEvent.eventName}. Change event.`
                  : 'Tag this capture to an event'
              }
              className="flex flex-1 items-center gap-2 truncate rounded-lg px-3 py-2 text-left hover:bg-primary/5 active:scale-[0.99]"
            >
              <CalendarDays className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="flex-1 truncate">
                <span className="text-muted-foreground">Event: </span>
                <span className="font-medium">{selectedEvent ? selectedEvent.eventName : 'none'}</span>
              </span>
            </button>
            {selectedEvent && (
              <button
                type="button"
                aria-label="Drop event tag"
                onClick={() => selectEvent(null)}
                className="rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}

        {/* EVENT PICKER OVERLAY — replaces the step content while open. */}
        {showEventPicker && (
          <EventPicker
            selected={selectedEvent}
            onSelect={selectEvent}
            onClose={() => setShowEventPicker(false)}
          />
        )}

        {/* MENU */}
        {!showEventPicker && step.kind === 'menu' && (
          <div className="grid grid-cols-2 gap-2">
            {MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                disabled={m.disabled}
                onClick={() => selectMode(m.key)}
                aria-label={m.label}
                className={`flex flex-col items-start gap-1 rounded-lg border border-border p-3 text-left ${
                  m.disabled
                    ? 'cursor-not-allowed bg-muted/40 opacity-60'
                    : 'bg-background hover:border-primary hover:bg-primary/5 active:scale-[0.98]'
                }`}
              >
                <m.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium leading-tight">{m.label}</span>
                {m.hint && (
                  <span className="text-[11px] text-muted-foreground">{m.hint}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* CONSENT */}
        {!showEventPicker && step.kind === 'consent' && (
          <ConsentGate
            onConfirm={(consent) => setStep({ kind: 'record', mode: step.mode, consent })}
            onNotesOnly={() => {
              setTextValue('');
              setStep({ kind: 'link' });
              // Drop straight into the text editor; submit marks consent_declined.
            }}
            onCancel={() => setStep({ kind: 'menu' })}
          />
        )}

        {/* RECORD */}
        {!showEventPicker && step.kind === 'record' && (
          <div className="flex flex-col gap-3">
            {!online && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  You&apos;re offline. Recording still works — the audio is saved on this device and
                  uploads automatically when you reconnect.
                </span>
              </div>
            )}
            <Recorder
              title={step.mode === 'memo' ? 'Voice memo' : 'Recording meeting'}
              onComplete={(rec) => void handleRecordingComplete(step.mode, step.consent, rec)}
            />
          </div>
        )}

        {/* LINK / TEXT */}
        {!showEventPicker && step.kind === 'link' && (
          <div className="flex flex-col gap-4">
            {!online && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  You&apos;re offline. Links need a connection, but a typed note saves now and syncs
                  later.
                </span>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="capture-link" className="text-sm">
                Paste a link
              </Label>
              <Input
                id="capture-link"
                type="url"
                inputMode="url"
                placeholder="https://…"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
              />
              <Button
                type="button"
                onClick={() => void submitLink()}
                disabled={!linkValue.trim() || submittingLink || !online}
                className="mt-1 w-full"
              >
                {submittingLink ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : !online ? (
                  'Offline — links need a connection'
                ) : (
                  'Ingest link'
                )}
              </Button>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="capture-text" className="text-sm">
                Type a note
              </Label>
              <Textarea
                id="capture-text"
                rows={4}
                placeholder="Jot down what you want to remember…"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => void submitNotesOnly(false)}
                disabled={!textValue.trim()}
                className="mt-1 w-full"
              >
                Save note
              </Button>
            </div>
          </div>
        )}

        {/* UPLOADING */}
        {!showEventPicker && step.kind === 'uploading' && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Saving your capture…</p>
          </div>
        )}

        {/* DONE */}
        {!showEventPicker && step.kind === 'done' && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="rounded-full bg-primary/10 p-3 text-primary">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <p className="text-sm font-medium">Capture saved</p>
            <p className="text-xs text-muted-foreground">
              {statusLabel ? `Status: ${statusLabel}` : 'Processing in the background…'}
            </p>
            {statusFromCache && (
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                <WifiOff className="h-3 w-3" aria-hidden="true" />
                {online ? 'Last known status — reconnecting…' : "Offline — last known status. We'll keep updating when you reconnect."}
              </p>
            )}
            <div className="mt-2 flex w-full flex-col gap-2">
              {step.sourceId && (
                <Button
                  type="button"
                  onClick={() => handleViewCapture(step.sourceId)}
                  className="w-full"
                >
                  View capture
                </Button>
              )}
              <Button
                type="button"
                variant={step.sourceId ? 'outline' : 'default'}
                onClick={handleClose}
                className="w-full"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* QUEUED (audio captured offline — safely staged for upload on reconnect) */}
        {!showEventPicker && step.kind === 'queued' && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="rounded-full bg-amber-500/10 p-3 text-amber-600 dark:text-amber-400">
              <WifiOff className="h-7 w-7" />
            </span>
            <p className="text-sm font-medium">Saved — waiting to upload</p>
            <p className="text-xs text-muted-foreground">
              You&apos;re offline. Your capture is stored safely on this device and uploads
              automatically as soon as you&apos;re back online.
            </p>
            <Button type="button" onClick={handleClose} className="mt-2 w-full">
              Done
            </Button>
          </div>
        )}

        {/* ERROR */}
        {!showEventPicker && step.kind === 'error' && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="rounded-full bg-destructive/10 p-3 text-destructive">
              <AlertTriangle className="h-7 w-7" />
            </span>
            <p className="text-sm font-medium">Something went wrong</p>
            <p className="text-xs text-muted-foreground">{step.message}</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep({ kind: 'menu' })}
              className="mt-2 w-full"
            >
              Back to capture
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
